import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Package, User, Calendar, MapPin, ClipboardList, Euro, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { RecepcionMaterialDocumentos } from '@/components/recepciones/RecepcionMaterialDocumentos';
import { useToast } from '@/hooks/use-toast';

interface Recepcion {
  id: string;
  fecha_recepcion: string;
  ubicacion: string;
  proveedor: string | null;
  notas: string | null;
  usuario_id: string;
  orden_trabajo_id: string | null;
  importe_total: number | null;
  iva_porcentaje: number | null;
  numero_albaran: string | null;
  fecha_valoracion: string | null;
  usuario_nombre?: string;
  ot_numero?: string;
  ot_titulo?: string;
}

interface Props { obraId: string }

const fmt = (n: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);

export const ObraMaterialesTab: React.FC<Props> = ({ obraId }) => {
  const { user, perfil } = useAuth();
  const { toast } = useToast();
  const isOffice = perfil?.rol === 'admin' || perfil?.rol === 'gestor';
  const [recepciones, setRecepciones] = useState<Recepcion[]>([]);
  const [loading, setLoading] = useState(true);
  const [valorando, setValorando] = useState<Recepcion | null>(null);
  const [valForm, setValForm] = useState({ importe_total: '', iva_porcentaje: '21', numero_albaran: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await (supabase.from('recepciones_material' as any) as any)
        .select('*').eq('obra_id', obraId).order('fecha_recepcion', { ascending: false });
      if (error) throw error;
      const rows = (data || []) as Recepcion[];

      const userIds = Array.from(new Set(rows.map(r => r.usuario_id).filter(Boolean)));
      const otIds = Array.from(new Set(rows.map(r => r.orden_trabajo_id).filter(Boolean) as string[]));

      const [usuariosRes, otsRes] = await Promise.all([
        userIds.length
          ? supabase.from('perfiles').select('user_id, nombre').in('user_id', userIds)
          : Promise.resolve({ data: [] as any[] }),
        otIds.length
          ? supabase.from('ordenes_trabajo').select('id, numero_ot, titulo').in('id', otIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);

      const userMap = new Map<string, string>();
      (usuariosRes.data || []).forEach((u: any) => userMap.set(u.user_id, u.nombre));
      const otMap = new Map<string, { numero_ot: string; titulo: string }>();
      (otsRes.data || []).forEach((o: any) => otMap.set(o.id, { numero_ot: o.numero_ot, titulo: o.titulo }));

      setRecepciones(rows.map(r => ({
        ...r,
        usuario_nombre: userMap.get(r.usuario_id),
        ot_numero: r.orden_trabajo_id ? otMap.get(r.orden_trabajo_id)?.numero_ot : undefined,
        ot_titulo: r.orden_trabajo_id ? otMap.get(r.orden_trabajo_id)?.titulo : undefined,
      })));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [obraId]);

  const pendientes = useMemo(() => recepciones.filter(r => r.importe_total == null), [recepciones]);
  const valoradas = useMemo(() => recepciones.filter(r => r.importe_total != null), [recepciones]);
  const totalValorado = useMemo(
    () => valoradas.reduce((s, r) => s + Number(r.importe_total || 0), 0),
    [valoradas]
  );

  const openValorar = (r: Recepcion) => {
    setValorando(r);
    setValForm({
      importe_total: r.importe_total != null ? String(r.importe_total) : '',
      iva_porcentaje: r.iva_porcentaje != null ? String(r.iva_porcentaje) : '21',
      numero_albaran: r.numero_albaran || '',
    });
  };

  const guardarValoracion = async () => {
    if (!valorando || !user) return;
    const importe = parseFloat(valForm.importe_total);
    if (!importe || importe < 0) {
      toast({ title: 'Importe inválido', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const { error } = await (supabase.from('recepciones_material' as any) as any)
        .update({
          importe_total: importe,
          iva_porcentaje: valForm.iva_porcentaje ? Number(valForm.iva_porcentaje) : null,
          numero_albaran: valForm.numero_albaran.trim() || null,
          fecha_valoracion: new Date().toISOString(),
          valorado_por: user.id,
        })
        .eq('id', valorando.id);
      if (error) throw error;
      toast({ title: 'Albarán valorado' });
      setValorando(null);
      load();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const renderCard = (r: Recepcion) => (
    <Card key={r.id}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="space-y-1 min-w-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <Package className="h-4 w-4" />
              {r.proveedor || 'Sin proveedor'}
              {r.numero_albaran && <span className="text-xs text-muted-foreground">· #{r.numero_albaran}</span>}
            </CardTitle>
            <CardDescription className="flex flex-wrap gap-x-4 gap-y-1">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {format(new Date(r.fecha_recepcion), "dd/MM/yyyy HH:mm", { locale: es })}
              </span>
              {r.usuario_nombre && (
                <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{r.usuario_nombre}</span>
              )}
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {r.ubicacion === 'obra' ? 'En obra' : 'Almacén'}
              </span>
              {r.ot_numero && (
                <span className="flex items-center gap-1">
                  <ClipboardList className="h-3.5 w-3.5" />{r.ot_numero} — {r.ot_titulo}
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1">
            {r.importe_total != null ? (
              <Badge variant="secondary" className="font-semibold text-sm">
                <Euro className="h-3 w-3 mr-1" />{fmt(Number(r.importe_total))}
                {r.iva_porcentaje != null && <span className="ml-1 text-xs opacity-70">+{r.iva_porcentaje}% IVA</span>}
              </Badge>
            ) : (
              <Badge variant="outline" className="border-amber-500/50 text-amber-600">
                <AlertTriangle className="h-3 w-3 mr-1" />Sin valorar
              </Badge>
            )}
            {isOffice && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openValorar(r)}>
                {r.importe_total != null ? 'Editar importe' : 'Añadir importe'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {r.notas && (<div className="text-sm bg-muted p-3 rounded-md whitespace-pre-wrap">{r.notas}</div>)}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <RecepcionMaterialDocumentos recepcionId={r.id} tipo="albaran" readonly />
          <RecepcionMaterialDocumentos recepcionId={r.id} tipo="material" readonly />
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <Card><CardContent className="py-8 text-center text-muted-foreground">Cargando…</CardContent></Card>;
  }

  if (recepciones.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Package className="h-5 w-5" /> Materiales recibidos</CardTitle>
          <CardDescription>Recepciones de material vinculadas a esta obra</CardDescription>
        </CardHeader>
        <CardContent className="py-10 text-center text-muted-foreground">
          No hay materiales recibidos para esta obra.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Resumen */}
      <Card>
        <CardContent className="pt-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm text-muted-foreground">Materiales valorados</div>
            <div className="text-2xl font-bold">{fmt(totalValorado)}</div>
          </div>
          {pendientes.length > 0 && (
            <Badge variant="outline" className="border-amber-500/50 text-amber-600">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {pendientes.length} {pendientes.length === 1 ? 'albarán' : 'albaranes'} sin valorar
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* Pendientes destacados */}
      {pendientes.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-amber-600 flex items-center gap-1">
            <AlertTriangle className="h-4 w-4" /> Albaranes pendientes de valorar
          </h3>
          {pendientes.map(renderCard)}
        </div>
      )}

      {/* Valorados */}
      {valoradas.length > 0 && (
        <div className="space-y-2">
          {pendientes.length > 0 && <h3 className="text-sm font-semibold">Albaranes valorados</h3>}
          {valoradas.map(renderCard)}
        </div>
      )}

      {/* Dialog valorar */}
      <Dialog open={!!valorando} onOpenChange={(o) => !o && setValorando(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Valorar albarán</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Nº albarán</Label>
              <Input value={valForm.numero_albaran}
                onChange={(e) => setValForm({ ...valForm, numero_albaran: e.target.value })}
                placeholder="Opcional" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>Importe (€) *</Label>
                <Input type="number" step="0.01" min="0"
                  value={valForm.importe_total}
                  onChange={(e) => setValForm({ ...valForm, importe_total: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>% IVA</Label>
                <Input type="number" step="0.01" min="0"
                  value={valForm.iva_porcentaje}
                  onChange={(e) => setValForm({ ...valForm, iva_porcentaje: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setValorando(null)}>Cancelar</Button>
            <Button onClick={guardarValoracion} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
